/**
 * useAiChatSession — Hook for sending messages and receiving streamed responses.
 *
 * Manages IPC communication with the Claude CLI runner in the Electron main process.
 * Parses stream events into AiChatMessage objects in the store.
 * Persists sessionId to workspace tab content for session restore.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useAiChatStore } from '../store/aiChatStore';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { buildSystemPrompt } from '../utils/contextBuilder';
import type { AiChatContext } from '../utils/contextBuilder';
import type { AiChatMessage, AiChatContentSegment, AiChatToolCall, AiChatStreamEvent, AiChatDoneEvent, AiChatErrorEvent } from '../types';
import { getHumanToolName } from '../utils/toolMeta';
import { incrementMessageCount } from '../services/messageCounter';

/** Stuck session timeout (configurable for E2E tests via window.__AI_CHAT_STUCK_TIMEOUT_MS) */
const getStuckTimeoutMs = () =>
  (window as unknown as Record<string, unknown>).__AI_CHAT_STUCK_TIMEOUT_MS as number ?? 60_000;

type UseAiChatSessionOptions = {
  tabId: string;
  tabType: 'query' | 'notebook' | 'plsql-editor';
  engine?: string;
  serverHost?: string;
  database?: string;
  schema?: string;
  connectionId?: string;
  hasConnection: boolean;
  getEditorContent?: () => string;
  getSelectedText?: () => string;
  // Notebook-specific
  getCells?: () => AiChatContext['cells'];
  getActiveCellIndex?: () => number | undefined;
  getActiveCellSource?: () => string | undefined;
  getCellCount?: () => number;
  // PL/SQL Editor-specific
  getPlSqlObjectInfo?: () => {
    objectName: string;
    schema: string;
    objectType: string;
    sourceView: 'spec' | 'body';
    members?: Array<{ name: string; type: 'procedure' | 'function' }>;
    compilationStatus?: 'valid' | 'invalid';
    compilationErrors?: Array<{ line: number; text: string }>;
  };
  getPlSqlDebugInfo?: () => {
    debugState: string;
    currentLine?: number;
    callStack?: Array<{ frameNumber: number; programName: string; programOwner: string; lineNumber: number }>;
    variables?: Array<{ name: string; value: string | null; type: string; scope: string }>;
    breakpoints?: Array<{ line: number; enabled: boolean; condition?: string }>;
    messages?: string[];
  } | null;
};

export function useAiChatSession(options: UseAiChatSessionOptions) {
  const { tabId, tabType, engine, serverHost, database, schema, connectionId, hasConnection } = options;
  const store = useAiChatStore;
  const toolCallsRef = useRef(new Map<string, AiChatToolCall>());
  const lastSentContextRef = useRef<{ connectionId?: string; database?: string; engine?: string } | null>(null);
  const stuckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectingWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset stuck session timer — called on every stream event
  const resetStuckTimer = useCallback(() => {
    if (stuckTimerRef.current) clearTimeout(stuckTimerRef.current);
    stuckTimerRef.current = setTimeout(() => {
      const state = store.getState().getTabState(tabId);
      if (state.phase === 'running') {
        store.getState().setStatusText(tabId, 'Session may be stuck');
      }
    }, getStuckTimeoutMs());
  }, [tabId, store]);

  const clearStuckTimer = useCallback(() => {
    if (stuckTimerRef.current) {
      clearTimeout(stuckTimerRef.current);
      stuckTimerRef.current = null;
    }
  }, []);

  const clearConnectingWatchdog = useCallback(() => {
    if (connectingWatchdogRef.current) {
      clearTimeout(connectingWatchdogRef.current);
      connectingWatchdogRef.current = null;
    }
  }, []);

  // Hydrate sessionId and sidebarOpen from tab content if store doesn't have them
  useEffect(() => {
    const tab = useWorkspaceStore.getState().tabs.find((t) => t.id === tabId);
    if (!tab) return;

    const content = tab.content as Record<string, unknown> | undefined;
    const tabState = store.getState().getTabState(tabId);

    if (!tabState.sessionId) {
      const persistedSessionId = content?.aiChatSessionId;
      if (typeof persistedSessionId === 'string' && persistedSessionId) {
        store.getState().setSessionId(tabId, persistedSessionId);
      }
    }

    if (content?.aiChatSidebarOpen === true) {
      store.getState().setSidebarOpen(tabId, true);
    }
  }, [tabId, store]);

  // Persist sidebarOpen to workspace tab content when it changes
  useEffect(() => {
    return store.subscribe((state, prevState) => {
      const open = state.tabs[tabId]?.sidebarOpen;
      const prevOpen = prevState.tabs[tabId]?.sidebarOpen;
      if (open !== prevOpen && open !== undefined) {
        useWorkspaceStore.getState().updateTabContent(tabId, { aiChatSidebarOpen: open });
      }
    });
  }, [tabId, store]);

  // Set up IPC listeners
  useEffect(() => {
    const api = window.electronAPI?.aiChat;
    if (!api) return;

    const cleanupStream = api.onStream((data: { tabId: string; event: AiChatStreamEvent }) => {
      if (data.tabId !== tabId) return;
      handleStreamEvent(data.event);
    });

    const cleanupDone = api.onDone((data: AiChatDoneEvent) => {
      if (data.tabId !== tabId) return;
      handleDone(data);
    });

    const cleanupError = api.onError((data: AiChatErrorEvent) => {
      if (data.tabId !== tabId) return;
      handleError(data);
    });

    return () => {
      cleanupStream();
      cleanupDone();
      cleanupError();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId]);

  // Cleanup on unmount: cancel running process + remove store state
  useEffect(() => {
    return () => {
      clearStuckTimer();
      clearConnectingWatchdog();
      const api = window.electronAPI?.aiChat;
      const state = store.getState().getTabState(tabId);
      if (state.isLoading && api) {
        api.cancel(tabId).catch(() => { /* ignore */ });
      }
      store.getState().removeTab(tabId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId]);

  const handleStreamEvent = useCallback((event: AiChatStreamEvent) => {
    resetStuckTimer();
    clearConnectingWatchdog();

    switch (event.type) {
      case 'system': {
        // Capture sessionId early from the system event
        store.getState().setSessionId(tabId, event.sessionId);
        break;
      }
      case 'thinking': {
        store.getState().setStatusText(tabId, 'Thinking...');
        store.getState().updateLastAssistantMessage(tabId, (msg) => {
          const segments = [...msg.segments];
          const lastSeg = segments[segments.length - 1];
          if (lastSeg && lastSeg.type === 'thinking') {
            segments[segments.length - 1] = {
              type: 'thinking',
              thinking: lastSeg.thinking + event.thinking,
              isStreaming: true,
            };
          } else {
            segments.push({ type: 'thinking', thinking: event.thinking, isStreaming: true });
          }
          return { ...msg, segments };
        });
        break;
      }
      case 'text': {
        store.getState().setStatusText(tabId, 'Thinking...');
        // Mark any preceding thinking segment as no longer streaming
        store.getState().updateLastAssistantMessage(tabId, (msg) => {
          const segments = [...msg.segments];
          for (let i = 0; i < segments.length; i++) {
            if (segments[i].type === 'thinking' && (segments[i] as { isStreaming?: boolean }).isStreaming) {
              segments[i] = { ...segments[i], isStreaming: false } as typeof segments[number];
            }
          }
          const lastSeg = segments[segments.length - 1];
          if (lastSeg && lastSeg.type === 'text') {
            segments[segments.length - 1] = { type: 'text', text: lastSeg.text + event.text };
          } else {
            segments.push({ type: 'text', text: event.text });
          }
          return {
            ...msg,
            content: msg.content + event.text,
            segments,
          };
        });
        break;
      }
      case 'tool_use': {
        store.getState().setStatusText(tabId, `Using ${getHumanToolName(event.name)}...`);
        const toolCall: AiChatToolCall = {
          id: event.id,
          name: event.name,
          input: event.input,
          isRunning: true,
          startedAt: Date.now(),
        };
        toolCallsRef.current.set(event.id, toolCall);
        store.getState().updateLastAssistantMessage(tabId, (msg) => {
          const segments: AiChatContentSegment[] = [...msg.segments, { type: 'tool_use', toolCall }];
          return { ...msg, segments };
        });
        break;
      }
      case 'tool_result': {
        const tc = toolCallsRef.current.get(event.toolUseId);
        if (tc) {
          tc.isRunning = false;
          tc.result = { content: event.content, isError: event.isError };
        }
        store.getState().setStatusText(tabId, 'Thinking...');
        store.getState().updateLastAssistantMessage(tabId, (msg) => {
          const segments = msg.segments.map((seg) => {
            if (seg.type === 'tool_use' && seg.toolCall.id === event.toolUseId) {
              return {
                ...seg,
                toolCall: {
                  ...seg.toolCall,
                  isRunning: false,
                  result: { content: event.content, isError: event.isError },
                },
              };
            }
            return seg;
          });
          return { ...msg, segments };
        });
        break;
      }
      case 'error': {
        const errorMsg: AiChatMessage = {
          id: `msg-${Date.now()}-err`,
          role: 'system',
          content: event.message,
          segments: [{ type: 'text', text: event.message }],
          timestamp: Date.now(),
        };
        store.getState().addMessage(tabId, errorMsg);
        break;
      }
    }
  }, [tabId, store, resetStuckTimer, clearConnectingWatchdog]);

  const handleDone = useCallback((data: AiChatDoneEvent) => {
    clearStuckTimer();
    clearConnectingWatchdog();
    store.getState().setLoading(tabId, false);
    store.getState().setStatusText(tabId, null);

    if (data.sessionId) {
      store.getState().setSessionId(tabId, data.sessionId);
      // Persist sessionId to workspace tab content for session save/restore
      useWorkspaceStore.getState().updateTabContent(tabId, { aiChatSessionId: data.sessionId });
    }

    // Populate lastRunMetadata for session metadata display
    if (data.durationMs != null && data.numTurns != null) {
      store.getState().setLastRunMetadata(tabId, {
        durationMs: data.durationMs,
        numTurns: data.numTurns,
      });
    }

    // Handle max turns
    if (data.subtype === 'error_max_turns') {
      store.getState().setPhase(tabId, 'completed');
      const maxTurnsMsg: AiChatMessage = {
        id: `msg-${Date.now()}-maxturns`,
        role: 'system',
        content: 'Claude reached the maximum number of tool calls. You can send another message to continue.',
        segments: [{ type: 'text', text: 'Claude reached the maximum number of tool calls. You can send another message to continue.' }],
        timestamp: Date.now(),
      };
      store.getState().addMessage(tabId, maxTurnsMsg);
    } else {
      store.getState().setPhase(tabId, 'completed');
    }

    // Handle permission denials
    if (data.permissionDenials && data.permissionDenials.length > 0) {
      const tools = data.permissionDenials.join(', ');
      const denialMsg: AiChatMessage = {
        id: `msg-${Date.now()}-permdeny`,
        role: 'system',
        content: `Some tools were blocked: ${tools}`,
        segments: [{ type: 'text', text: `Some tools were blocked: ${tools}` }],
        timestamp: Date.now(),
      };
      store.getState().addMessage(tabId, denialMsg);
    }

    toolCallsRef.current.clear();
  }, [tabId, store, clearStuckTimer, clearConnectingWatchdog]);

  const handleError = useCallback((data: AiChatErrorEvent) => {
    clearStuckTimer();
    clearConnectingWatchdog();
    store.getState().setLoading(tabId, false);
    store.getState().setError(tabId, data.message);
    store.getState().setPhase(tabId, 'error');
    store.getState().setStatusText(tabId, null);
    const errorMsg: AiChatMessage = {
      id: `msg-${Date.now()}-err`,
      role: 'system',
      content: data.message,
      segments: [{ type: 'text', text: data.message }],
      timestamp: Date.now(),
    };
    store.getState().addMessage(tabId, errorMsg);
    toolCallsRef.current.clear();
  }, [tabId, store, clearStuckTimer, clearConnectingWatchdog]);

  const sendMessage = useCallback(async (message: string) => {
    const api = window.electronAPI?.aiChat;
    if (!api) return;

    const tabState = store.getState().getTabState(tabId);

    incrementMessageCount(tabState.activeAdapter ?? 'unknown');

    // Add user message to store
    const userMsg: AiChatMessage = {
      id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      role: 'user',
      content: message,
      segments: [{ type: 'text', text: message }],
      timestamp: Date.now(),
    };
    store.getState().addMessage(tabId, userMsg);

    // Add empty assistant message that will be filled by stream events
    const assistantMsg: AiChatMessage = {
      id: `msg-${Date.now()}-asst`,
      role: 'assistant',
      content: '',
      segments: [],
      timestamp: Date.now(),
    };
    store.getState().addMessage(tabId, assistantMsg);
    store.getState().setLoading(tabId, true);
    store.getState().setError(tabId, null);
    store.getState().setPhase(tabId, 'running');
    const adapterLabel = tabState.activeAdapter === 'codex' ? 'Codex' : 'Claude';
    const connectingText = `Connecting to ${adapterLabel}...`;
    store.getState().setStatusText(tabId, connectingText);
    store.getState().setLastRunMetadata(tabId, null);

    // Start stuck session timer
    resetStuckTimer();

    // 5-second watchdog: if still "Connecting..." after 5s, update status
    clearConnectingWatchdog();
    connectingWatchdogRef.current = setTimeout(() => {
      const state = store.getState().getTabState(tabId);
      if (state.phase === 'running' && state.statusText === connectingText) {
        store.getState().setStatusText(tabId, 'Waiting for response...');
      }
    }, 5000);

    // Build context
    const isResuming = !!tabState.sessionId;
    const plsqlInfo = options.getPlSqlObjectInfo?.();
    const debugInfo = options.getPlSqlDebugInfo?.();
    const ctx: AiChatContext = {
      tabId,
      connectionId,
      tabType,
      engine,
      serverHost,
      database,
      schema,
      hasConnection,
      isResuming,
      editorContent: options.getEditorContent?.() ?? '',
      selectedText: options.getSelectedText?.() ?? '',
      previousContext: isResuming ? lastSentContextRef.current : null,
      currentContext: { connectionId, database, engine },
      cells: options.getCells?.(),
      activeCellIndex: options.getActiveCellIndex?.(),
      activeCellSource: options.getActiveCellSource?.(),
      cellCount: options.getCellCount?.(),
      ...(plsqlInfo && {
        plsqlObjectName: plsqlInfo.objectName,
        plsqlSchema: plsqlInfo.schema,
        plsqlObjectType: plsqlInfo.objectType,
        plsqlSourceView: plsqlInfo.sourceView,
        plsqlMembers: plsqlInfo.members,
        plsqlCompilationStatus: plsqlInfo.compilationStatus,
        plsqlCompilationErrors: plsqlInfo.compilationErrors,
      }),
      ...(debugInfo && {
        plsqlDebugState: debugInfo.debugState as AiChatContext['plsqlDebugState'],
        plsqlCurrentLine: debugInfo.currentLine,
        plsqlCallStack: debugInfo.callStack,
        plsqlVariables: debugInfo.variables,
        plsqlBreakpoints: debugInfo.breakpoints,
        plsqlMessages: debugInfo.messages,
      }),
    };

    const systemPrompt = buildSystemPrompt(ctx);

    // Update last sent context
    lastSentContextRef.current = { connectionId, database, engine };

    const adapterName = tabState.activeAdapter ?? null;
    console.log(`[AI Chat] sendMessage: tabId=${tabId} sessionId=${tabState.sessionId ?? 'null'} isResuming=${isResuming} adapter=${adapterName ?? 'default'}`);
    await api.sendMessage({
      tabId,
      message,
      sessionId: tabState.sessionId,
      systemPrompt,
      adapterName,
    });
  }, [tabId, tabType, engine, serverHost, database, schema, connectionId, hasConnection, options, store, resetStuckTimer, clearConnectingWatchdog]);

  const cancel = useCallback(async () => {
    const api = window.electronAPI?.aiChat;
    if (!api) return;
    clearStuckTimer();
    clearConnectingWatchdog();
    await api.cancel(tabId);
    store.getState().setLoading(tabId, false);
    store.getState().setPhase(tabId, 'stopped');
    store.getState().setStatusText(tabId, null);
  }, [tabId, store, clearStuckTimer, clearConnectingWatchdog]);

  return { sendMessage, cancel };
}
